import { bookingsAPI } from 'api/bookingsAPI';
import type {
    BookingPrecheckRequestData,
    BookingPrecheckResponse,
} from 'api/models/BookingPrecheck.type';
import { useWizard } from 'components/RouteWizard/Wizard';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import useUserRole from './useUserRole';

export type PolicyMessage = {
  severity: 'success' | 'warning' | 'error'
  title: string
  body?: string
  status: string
  policyId?: number
  policyName?: string
  overridable?: boolean
}

type Money = {
  price: number
  vat: number
  currency: string
  total: number
}

type SpendValue = number | Money

const DEFAULT_CURRENCY = 'GBP'

const isMoney = (v: unknown): v is Money => {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as any).total === 'number' &&
    typeof (v as any).currency === 'string'
  )
}

const asTotalNumber = (v: SpendValue | undefined): number | undefined => {
  if (typeof v === 'number') return v
  if (isMoney(v)) return v.total
  return undefined
}

const currencyFrom = (v: SpendValue | undefined): string => {
  if (isMoney(v)) return v.currency || DEFAULT_CURRENCY
  return DEFAULT_CURRENCY
}

const formatMoney = (
  amount: number,
  currency = DEFAULT_CURRENCY,
  locale = 'en-GB',
): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}

export const formatCurrency = formatMoney

// Extract limit values from policy
const getLimitByBreachType = (
  policy: any,
  breachType?: string,
): number | undefined => {
  if (!breachType) {
    return policy?.spendLimitLifeTime ?? policy?.spendLimitLifetime
  }

  const limits: Record<string, string[]> = {
    daily: ['spendLimitPerDay', 'dailySpendLimit'],
    weekly: ['spendLimitPerWeek', 'weeklySpendLimit'],
    monthly: ['spendLimitPerMonth', 'monthlySpendLimit'],
    lifetime: ['spendLimitLifeTime', 'spendLimitLifetime'],
  }

  for (const keys of Object.values(limits)) {
    for (const key of keys) {
      if (policy?.[key]) return policy[key]
    }
  }

  return undefined
}

// Identify the breach type from the reason code
const identifyBreachType = (reasonCode?: string): string | undefined => {
  if (!reasonCode) return undefined
  const code = String(reasonCode).toUpperCase()

  if (code.includes('DISTANCE')) return 'DISTANCE'
  if (code.includes('SPEND_PER_DAY')) return 'SPEND_PER_DAY'
  if (code.includes('SPEND_PER_WEEK')) return 'SPEND_PER_WEEK'
  if (code.includes('SPEND_PER_MONTH')) return 'SPEND_PER_MONTH'
  if (code.includes('SPEND_LIFE_TIME') || code.includes('SPEND_LIFETIME'))
    return 'SPEND_LIFE_TIME'

  return undefined
}

const getSpendByPeriod = (
  decision: any,
  period: 'day' | 'week' | 'month' | 'lifeTime',
): { current?: number; projected?: number; currency: string } => {
  const spend = decision?.spend

  const userPeriodData =
    spend?.user?.[period] ??
    spend?.user?.[period === 'lifeTime' ? 'lifetime' : period]
  const groupPeriodData =
    spend?.group?.[period] ??
    spend?.group?.[period === 'lifeTime' ? 'lifetime' : period]

  // Prefer user over group, but use whichever has data
  const periodData = userPeriodData || groupPeriodData

  if (!periodData) {
    return {
      current: undefined,
      projected: undefined,
      currency: DEFAULT_CURRENCY,
    }
  }

  const current = asTotalNumber(periodData.current)
  const projected = asTotalNumber(periodData.projected)
  const currency = isMoney(periodData.current)
    ? currencyFrom(periodData.current)
    : isMoney(periodData.projected)
    ? currencyFrom(periodData.projected)
    : DEFAULT_CURRENCY

  return { current, projected, currency }
}

// Get period label for messages
const getPeriodLabel = (
  breachType: string,
):
  | { label: string; period: 'day' | 'week' | 'month' | 'lifeTime' }
  | undefined => {
  if (breachType.includes('PER_DAY')) {
    return { label: 'daily', period: 'day' }
  }
  if (breachType.includes('PER_WEEK')) {
    return { label: 'weekly', period: 'week' }
  }
  if (breachType.includes('PER_MONTH')) {
    return { label: 'monthly', period: 'month' }
  }
  if (breachType.includes('LIFE_TIME') || breachType.includes('LIFETIME')) {
    return { label: 'lifetime', period: 'lifeTime' }
  }
  return undefined
}

const policyLabel = (policy?: { name?: string } | null): string => {
  return policy?.name ? `“${policy.name}”` : 'your travel policy'
}

const isDistanceLimitExceededBreach = (breach: any): boolean => {
  const code = String(breach?.reason?.code ?? '')
    .trim()
    .toUpperCase()

  return code.includes('DISTANCE')
}

const resolveSeverity = (
  decision: BookingPrecheckResponse,
): PolicyMessage['severity'] => {
  if (decision?.status === 'COMPLIANT' || decision?.status === 'NO_POLICY')
    return 'success'
  if (decision?.status === 'NEAR_LIMIT') return 'warning'
  if (decision?.status === 'BREACH' || decision?.status === 'DENIED')
    return 'error'
  return decision?.allowed ? 'warning' : 'error'
}

const resolveApiPolicyMessage = (
  decision: BookingPrecheckResponse,
): { title?: string; body?: string } | null => {
  const raw = decision?.policyMessage ?? decision?.message

  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    return trimmed ? { title: trimmed } : null
  }

  if (raw && typeof raw === 'object') {
    const title =
      (raw as any).title || (raw as any).message || (raw as any).text
    const body =
      (raw as any).body || (raw as any).detail || (raw as any).details
    if (typeof title === 'string' || typeof body === 'string') {
      return {
        title: typeof title === 'string' ? title.trim() : undefined,
        body: typeof body === 'string' ? body.trim() : undefined,
      }
    }
  }

  // Fall back to breach reason message if available
  const breachMessage = decision?.breach?.reason?.message
  if (breachMessage && typeof breachMessage === 'string') {
    const trimmed = breachMessage.trim()
    return trimmed ? { body: trimmed } : null
  }

  return null
}

export const buildPolicyMessage = (
  decision: BookingPrecheckResponse,
  opts?: {
    locale?: string
    showCompliantMessage?: boolean
    clientCurrency?: string
  },
): PolicyMessage | null => {
  const locale = opts?.locale ?? 'en-GB'
  const clientCurrency = opts?.clientCurrency
  const policy: any = (decision as any)?.policy
  const name = policyLabel(policy)

  const apiMessage = resolveApiPolicyMessage(decision)
  if (
    (decision?.status === 'COMPLIANT' || decision?.status === 'NO_POLICY') &&
    !opts?.showCompliantMessage
  ) {
    return null
  }

  if (apiMessage && (apiMessage.title || apiMessage.body)) {
    const title = apiMessage.title || apiMessage.body || ''
    const body =
      apiMessage.title && apiMessage.body ? apiMessage.body : undefined
    return {
      severity: resolveSeverity(decision),
      title,
      body,
      status: decision?.status ?? 'UNKNOWN',
      policyId: policy?.id,
      policyName: policy?.name,
      overridable: (decision as any)?.overridable ?? (decision as any)?.allowed,
    }
  }

  if (decision?.status === 'COMPLIANT' || decision?.status === 'NO_POLICY') {
    return {
      severity: 'success',
      title: `All good - this booking is within policy.`,
      status: decision.status,
      policyId: policy?.id,
      policyName: policy?.name,
      overridable: (decision as any)?.overridable,
    }
  }

  if (decision?.status === 'NEAR_LIMIT') {
    const nearBreachArray = decision?.nearBreach
    const nearBreach = Array.isArray(nearBreachArray)
      ? nearBreachArray[0]
      : nearBreachArray
    if (!nearBreach) {
      return {
        severity: 'warning',
        title: `Heads up - this booking is close to your travel policy limit.`,
        body: `You may need approval soon.`,
        status: decision.status,
        policyId: policy?.id,
        policyName: policy?.name,
        overridable:
          (decision as any)?.overridable ?? (decision as any)?.allowed,
      }
    }

    const breachType = identifyBreachType(nearBreach.breachType)
    const periodInfo = breachType ? getPeriodLabel(breachType) : undefined
    const period = periodInfo?.period || 'lifeTime'
    const periodLabel = periodInfo?.label || 'lifetime'

    const { projected, currency } = getSpendByPeriod(decision, period)
    const limit = getLimitByBreachType(policy, breachType)
    const amountRemaining = asTotalNumber(nearBreach.amountRemaining)
    const messageCurrency = clientCurrency || currency

    const projectedText =
      typeof projected === 'number'
        ? formatMoney(projected, messageCurrency, locale)
        : 'a higher total'

    const remainingText =
      typeof amountRemaining === 'number'
        ? formatMoney(amountRemaining, messageCurrency, locale)
        : 'a small amount'

    const limitText =
      typeof limit === 'number'
        ? formatMoney(limit, messageCurrency, locale)
        : 'the limit'

    return {
      severity: 'warning',
      title: `Heads up - this booking is close to your travel policy limit.`,
      body: `Your ${periodLabel} spend is projected to be ${projectedText} (limit: ${limitText}), leaving ${remainingText} remaining. You can continue, but you may need approval soon.`,
      status: decision.status,
      policyId: policy?.id,
      policyName: policy?.name,
      overridable: (decision as any)?.overridable ?? (decision as any)?.allowed,
    }
  }

  // Handle BREACH - hard limit exceeded
  if (decision?.status === 'BREACH') {
    const breach: any = (decision as any)?.breach
    const reasonCode = breach?.reason?.code as string | undefined
    const reasonMsg = breach?.reason?.message as string | undefined

    // DISTANCE BREACH - highest priority, cannot book
    if (isDistanceLimitExceededBreach(breach)) {
      const amountOver = asTotalNumber(breach?.amountOver)
      const overText =
        typeof amountOver === 'number'
          ? `${amountOver} mile${amountOver === 1 ? '' : 's'}`
          : 'the allowed distance'

      return {
        severity: 'error',
        title: `Travel Policy: ${name}.`,
        body:
          reasonMsg?.trim() ||
          `This journey is ${overText} over the allowed distance limit. You cannot book this journey. Please contact an authorised user to discuss options.`,
        status: decision.status,
        policyId: policy?.id,
        policyName: policy?.name,
        overridable: (decision as any)?.overridable,
      }
    }

    // SPEND BREACH - identify which limit was exceeded
    const breachType = identifyBreachType(reasonCode)
    const periodInfo = breachType ? getPeriodLabel(breachType) : undefined
    const period = periodInfo?.period || 'lifeTime'
    const periodLabel = periodInfo?.label || 'lifetime'

    const { projected, currency } = getSpendByPeriod(decision, period)
    const limit = getLimitByBreachType(policy, breachType)
    const amountOver = asTotalNumber(breach?.amountOver)
    const messageCurrency = clientCurrency || currency

    const projectedText =
      typeof projected === 'number'
        ? formatMoney(projected, messageCurrency, locale)
        : 'a higher total'

    const limitText =
      typeof limit === 'number'
        ? formatMoney(limit, messageCurrency, locale)
        : 'the limit'

    const overText =
      typeof amountOver === 'number'
        ? formatMoney(amountOver, messageCurrency, locale)
        : 'more than allowed'

    return {
      severity: 'error',
      title: `Travel Policy: ${name}.`,
      body:
        reasonMsg?.trim() ||
        `Your ${periodLabel} spend would be ${projectedText} (limit: ${limitText}), which is ${overText} inc. VAT over. Please contact an authorised user to approve or adjust the booking.`,
      status: decision.status,
      policyId: policy?.id,
      policyName: policy?.name,
      overridable: (decision as any)?.overridable,
    }
  }

  return {
    severity: (decision as any)?.allowed ? 'warning' : 'error',
    title: (decision as any)?.allowed
      ? `Policy check: booking is allowed under ${name}.`
      : `Policy check: booking is not allowed under ${name}.`,
    body: `Status: ${decision?.status ?? 'UNKNOWN'}.`,
    status: String(decision?.status ?? 'UNKNOWN'),
    policyId: policy?.id,
    policyName: policy?.name,
    overridable: (decision as any)?.overridable,
  }
}

export const buildPolicyDialogText = (
  decision: BookingPrecheckResponse,
  opts?: {
    locale?: string
    showCompliantMessage?: boolean
    clientCurrency?: string
  },
): string => {
  const msg = buildPolicyMessage(decision, opts)
  if (!msg) return ''
  return msg.body ? `${msg.title}\n\n${msg.body}` : msg.title
}

const useTravelPolicy = () => {
  const currentUser = useSelector((state: any) => state.users.user)
  const currentClient = useSelector((state: any) => state.clients.client)
  const bookingPreview = useSelector(
    (state: any) => state.bookings?.newBookingPreview,
  )
  const { data } = useWizard()

  const { currentUserRoles, validateAccess, hasRole, filteredRoles } =
    useUserRole()

  const [precheckResult, setPrecheckResult] =
    useState<BookingPrecheckResponse | null>(null)
  const [isPrecheckLoading, setIsPrecheckLoading] = useState(false)
  const [precheckError, setPrecheckError] = useState<string | null>(null)

  const clientCurrency = currentClient?.billing?.currency

  const toBookingTypeName = (value: unknown) => {
    if (!value) return ''
    const raw = String(value)
    return raw.length ? raw.charAt(0).toUpperCase() + raw.slice(1) : ''
  }

  const resolvePickupDateTime = () => {
    const when = data?.plan?.outbound?.when
    if (!when) return ''
    if (when instanceof Date) return when.toISOString()
    return String(when)
  }

  const resolveEstimatedPrice = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (value && typeof value === 'object') {
      const maybeTotal = (value as any).total
      if (typeof maybeTotal === 'number' && Number.isFinite(maybeTotal))
        return maybeTotal

      const maybePrice = (value as any).price
      if (typeof maybePrice === 'number' && Number.isFinite(maybePrice))
        return maybePrice
    }
    return null
  }

  const resolveEstimatedDistanceInMiles = (): number | undefined => {
    const candidates = [
      data?.preview?.estimatedDistanceInMiles,
      data?.preview?.estimatedJourneyDistance,
      (data?.preview as any)?.estimatedHJourneyDistance,
      bookingPreview?.estimatedDistanceInMiles,
      bookingPreview?.estimatedJourneyDistance,
      bookingPreview?.estimatedHJourneyDistance,
    ]

    for (const candidate of candidates) {
      if (typeof candidate === 'number' && Number.isFinite(candidate))
        return candidate
      if (typeof candidate === 'string') {
        const parsed = Number(candidate)
        if (Number.isFinite(parsed)) return parsed
      }
    }

    return undefined
  }

  const precheckBooking = async (precheckData: BookingPrecheckRequestData) => {
    setIsPrecheckLoading(true)
    setPrecheckError(null)

    try {
      const resp = await bookingsAPI.precheckBooking(precheckData)
      setPrecheckResult(resp.data)
      return resp.data
    } catch (err: any) {
      const message =
        err?.response?.data?.error?.message ||
        err?.message ||
        'Unknown error occurred'
      setPrecheckError(message)
      setPrecheckResult(null)
      throw err
    } finally {
      setIsPrecheckLoading(false)
    }
  }

  const precheckWizardBooking = async (
    overrides: Partial<BookingPrecheckRequestData> = {},
  ) => {
    const bookingType = overrides.bookingType || toBookingTypeName(data?.type)

    const userId =
      overrides.userId ||
      data?.account?.mainPassenger?.userId ||
      currentUser?.userId

    const pickupDateTime =
      overrides.pickupDateTime || resolvePickupDateTime() || ''

    const estimatedPrice =
      overrides.estimatedPrice ??
      resolveEstimatedPrice(data?.preview?.estimatedPriceFull) ??
      resolveEstimatedPrice(data?.preview?.estimatedPrice)

    const estimatedDistanceInMiles =
      overrides.estimatedDistanceInMiles ?? resolveEstimatedDistanceInMiles()

    if (!bookingType) {
      throw new Error('Unable to precheck: bookingType is missing.')
    }
    if (!userId) {
      throw new Error('Unable to precheck: userId is missing.')
    }
    if (!pickupDateTime) {
      throw new Error('Unable to precheck: pickupDateTime is missing.')
    }
    if (estimatedPrice == null) {
      throw new Error('Unable to precheck: estimatedPrice is missing.')
    }

    return precheckBooking({
      bookingType,
      userId,
      pickupDateTime,
      estimatedDistanceInMiles,
      estimatedPrice,
    })
  }

  const buildPolicyMessageWithClientCurrency = (
    decision: BookingPrecheckResponse,
    opts?: {
      locale?: string
      showCompliantMessage?: boolean
    },
  ) => {
    return buildPolicyMessage(decision, {
      ...opts,
      clientCurrency,
    })
  }

  const buildPolicyDialogTextWithClientCurrency = (
    decision: BookingPrecheckResponse,
    opts?: {
      locale?: string
      showCompliantMessage?: boolean
    },
  ) => {
    return buildPolicyDialogText(decision, {
      ...opts,
      clientCurrency,
    })
  }

  return {
    currentUserRoles,
    validateAccess,
    hasRole,
    filteredRoles,
    precheckResult,
    precheckError,
    isPrecheckLoading,
    precheckBooking,
    precheckWizardBooking,
    buildPolicyMessage: buildPolicyMessageWithClientCurrency,
    buildPolicyDialogText: buildPolicyDialogTextWithClientCurrency,
  }
}

export default useTravelPolicy
